import React, { useEffect, useState } from 'react';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Checkbox, Col, Form, Modal, Radio, Row, Space } from 'antd-v5';
import { MdWarning } from 'react-icons/md';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { Button, Select, Text } from 'scouting-ui/lib';

import type { PersonRegistrationData } from '@appTypes/esb';
import type { ESBBaseError } from '@appTypes/esb/errors';
import { useGetLookupOptoutReasonsQuery } from '@modules/lookups/api';
import { useUpdateMembershipRegistrationMutation } from '@modules/rtk/esb-api';
import toastService from '@modules/toasts/service';
import { ConditionalRender } from '@shared/components';
import { intl } from '@shared/localization';

import styles from './EditParticipationModal.less';

interface EditParticipationModalProps {
  isOptedOut: boolean;
  visible: boolean;
  onCancel: () => void;
  personGuid: string;
  registration: Pick<
    PersonRegistrationData,
    'registrationId' | 'renewalStatus' | 'bsaOrderId' | 'organizationName'
  >;
}

interface ParticipationForm {
  Checks: string[];
  OptOutMethod: OptoutMethod;
  /** Reason ID */
  OptOutReason: number;
}

enum FormFields {
  Checks = 'Checks',
  OptOutMethod = 'OptOutMethod',
  OptOutReason = 'OptOutReason',
}

enum OptoutMethod {
  Immediate = 'Immediate',
  EndOfTerm = 'EndOfTerm',
}

/** Not participating after registration expires */
const END_OF_TERM_ID = 6;

const EditParticipationModal = (props: EditParticipationModalProps) => {
  const [form] = Form.useForm<ParticipationForm>();
  const method = Form.useWatch(FormFields.OptOutMethod, form);
  const values = Form.useWatch([], form);
  const { data: optoutReasons, isLoading } = useGetLookupOptoutReasonsQuery();
  const [submittable, setSubmittable] = useState<boolean>(false);

  const { isOptedOut, registration, personGuid, onCancel } = props;
  const [updateMembership, { isLoading: isUpdating }] =
    useUpdateMembershipRegistrationMutation();

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();

      const selectedMethod = values[FormFields.OptOutMethod];

      const renewalOptOutReasonsId =
        selectedMethod === OptoutMethod.EndOfTerm
          ? END_OF_TERM_ID
          : values[FormFields.OptOutReason];

      try {
        await updateMembership({
          personGuid: personGuid,
          body: { isAutoRenewalOptedOut: true, renewalOptOutReasonsId },
          registrationId: registration?.registrationId,
        }).unwrap();
        toastService.success(
          intl.formatMessage({
            id: 'youthMembership.optoutModal.update.success',
          }),
        );
        onCancel();
      } catch (error) {
        const err = error as FetchBaseQueryError;
        const data = err.data as ESBBaseError | undefined;
        if (err.status === 403) return;
        const errorMessage = data?.errorDesc;
        toastService.error(errorMessage);
      }
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const hasPendingOrder = !!registration.bsaOrderId;

  return (
    <Modal
      open={props.visible}
      onCancel={() => onCancel()}
      destroyOnClose
      title={<FormattedMessage id="youthMembership.optoutModal.title" />}
      width={900}
      footer={
        <Row justify="end">
          <Col flex={'auto'}>
            <ConditionalRender value={isOptedOut || hasPendingOrder}>
              <Button onClick={onCancel}>
                <FormattedMessage id="shared.close" />
              </Button>
            </ConditionalRender>
            <ConditionalRender value={!isOptedOut && !hasPendingOrder}>
              <Space>
                <Button
                  loading={isLoading}
                  onClick={onSubmit}
                  htmlType="submit"
                  disabled={isLoading || isUpdating || !submittable}
                >
                  <FormattedMessage id="shared.confirm" />
                </Button>
                <Button type="default" disabled={isLoading} onClick={onCancel}>
                  <FormattedMessage id="shared.cancel" />
                </Button>
              </Space>
            </ConditionalRender>
          </Col>
        </Row>
      }
    >
      <Form clearOnDestroy form={form} initialValues={{}}>
        <ConditionalRender value={!isOptedOut && hasPendingOrder}>
          <Text>
            <FormattedHTMLMessage id="youthMembership.optoutModal.withPendingOrder" />
          </Text>
        </ConditionalRender>
        <ConditionalRender value={!isOptedOut && !hasPendingOrder}>
          <Space direction="vertical">
            <Text>
              <FormattedHTMLMessage id="youthMembership.optoutModal.section-1" />
            </Text>
            <Form.Item
              name={FormFields.Checks}
              className={styles.formItemNoMargin}
              rules={[
                {
                  async validator(_rule, value) {
                    const values = new Set(value);
                    const expectedValues = ['check-1', 'check-2', 'check-3'];
                    const isValid = expectedValues.every(item =>
                      values.has(item),
                    );
                    if (!isValid) {
                      throw new Error(
                        intl.formatMessage({
                          id: 'youthMembership.optoutModal.acknowledge',
                        }),
                      );
                    }
                  },
                },
              ]}
            >
              <Checkbox.Group
                options={[
                  {
                    label: (
                      <Text>
                        <FormattedHTMLMessage id="youthMembership.optoutModal.checkbox-1" />
                      </Text>
                    ),
                    value: 'check-1',
                  },
                  {
                    label: (
                      <Text>
                        <FormattedHTMLMessage id="youthMembership.optoutModal.checkbox-2" />
                      </Text>
                    ),
                    value: 'check-2',
                  },
                  {
                    label: (
                      <Text>
                        <FormattedHTMLMessage id="youthMembership.optoutModal.checkbox-3" />
                      </Text>
                    ),
                    value: 'check-3',
                  },
                ]}
              />
            </Form.Item>
            <Text>
              <FormattedHTMLMessage id="youthMembership.optoutModal.section-2" />
            </Text>
            <Text strong>
              <MdWarning className={styles.warningIcon} />{' '}
              <FormattedHTMLMessage id="youthMembership.optoutModal.section-3" />
            </Text>
            <Form.Item
              name={FormFields.OptOutMethod}
              className={styles.formItemNoMargin}
              required
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value={OptoutMethod.EndOfTerm}>
                  <FormattedMessage id="youthMembership.optoutModal.method-1" />
                </Radio>
                <Radio
                  value={OptoutMethod.Immediate}
                  className={styles.immediatelyRadio}
                >
                  <FormattedHTMLMessage id="youthMembership.optoutModal.method-2" />
                </Radio>
              </Radio.Group>
            </Form.Item>
            <ConditionalRender value={method === OptoutMethod.Immediate}>
              <Form.Item
                name={FormFields.OptOutReason}
                required
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: 'shared.form.error.isRequired',
                    }),
                  },
                ]}
              >
                <Select
                  popupMatchSelectWidth={false}
                  className={styles.reasonSelect}
                  placeholder={
                    <FormattedMessage id="youthMembership.optoutModal.optoutReason" />
                  }
                  options={optoutReasons
                    ?.filter(reason => reason.id !== END_OF_TERM_ID)
                    .map(reason => ({
                      label: reason.name,
                      value: reason.id,
                    }))}
                />
              </Form.Item>
            </ConditionalRender>
          </Space>
        </ConditionalRender>
        <ConditionalRender value={isOptedOut}>
          <Text>
            <FormattedHTMLMessage
              id="youthMembership.optoutModal.alreadyOptedOut"
              values={{ unitName: registration.organizationName }}
            />
          </Text>
        </ConditionalRender>
      </Form>
    </Modal>
  );
};

const EditParticipationModalContainer = (
  props: EditParticipationModalProps,
) => {
  if (!props.visible) return null;

  return <EditParticipationModal {...props} />;
};

export default EditParticipationModalContainer;
